/**
 * DEĞERLEME DAMGALARININ VE DAYANAKLARININ İNSAN DİLİ KARŞILIĞI.
 *
 * Ayrı bir dosya, çünkü burası raporun en kolay sessizleşen yeri: bir damga
 * kodu eklenip bu tabloya yazılmazsa arayüz onu ya hiç göstermez ya da ham
 * kodla gösterir. `Record<ValuationNoteCode, …>` olması bunu DERLEME hatasına
 * çeviriyor — yeni bir damga eklendiğinde bu dosya derlenmez ve unutulamaz.
 *
 * Metinler "ne oldu" değil "bu sayıya ne yapmalıyım" diyor: kullanıcı damgayı
 * gördüğünde defterinde mi bir eksik var, eşitleme mi beklemeli, yoksa sayı
 * doğru da yalnızca bilgilendirme mi — ayırt edebilmeli.
 */

import type {
  ValuationBasis,
  ValuationBasisKind,
  ValuationNoteCode,
} from "@parafon/contracts/local-api";
import { M, YEREL } from "../dil";

/**
 * Damganın AĞIRLIĞI.
 *
 * `danger` = bu pozisyonun bir sayısı ÜRETİLEMEDİ ya da eksik; toplamı
 * olduğu gibi okumak yanlış olur.
 * `warn`   = sayı var ama bir varsayım/eksiklik taşıyor.
 * `info`   = sayı sağlam; damga yalnızca hangi yolun seçildiğini söylüyor.
 */
export type NoteTone = "danger" | "warn" | "info";

export interface NoteText {
  readonly tone: NoteTone;
  readonly label: string;
  /** Kullanıcının ne yapacağı. Boşsa yapılacak bir şey yok. */
  readonly action: string;
}

/** Ton burada (yargı); etiket ve eylem metni sözlükte (`M.bicim.damga`). */
export const VALUATION_NOTE_TEXT: Readonly<Record<ValuationNoteCode, NoteText>> = {
  oversold: { tone: "danger", ...M.bicim.damga.oversold },
  transfer_cost_unknown: { tone: "warn", ...M.bicim.damga.transfer_cost_unknown },
  rescale_without_lots: { tone: "warn", ...M.bicim.damga.rescale_without_lots },
  mixed_currency: { tone: "info", ...M.bicim.damga.mixed_currency },
  nakit_hareketi_okunamadi: { tone: "danger", ...M.bicim.damga.nakit_hareketi_okunamadi },
  nakit_hareketi_cevrilemedi: { tone: "warn", ...M.bicim.damga.nakit_hareketi_cevrilemedi },
  para_birimi_taninmiyor: { tone: "danger", ...M.bicim.damga.para_birimi_taninmiyor },
  fiyat_yok: { tone: "danger", ...M.bicim.damga.fiyat_yok },
  kur_yok: { tone: "danger", ...M.bicim.damga.kur_yok },
  elle_deger_yok: { tone: "danger", ...M.bicim.damga.elle_deger_yok },
  para_birimi_celiskisi: { tone: "danger", ...M.bicim.damga.para_birimi_celiskisi },
  elle_fiyat_yoksayildi: { tone: "warn", ...M.bicim.damga.elle_fiyat_yoksayildi },
  kapali_pozisyon: { tone: "info", ...M.bicim.damga.kapali_pozisyon },
  nakit_tutar_okunamadi: { tone: "danger", ...M.bicim.damga.nakit_tutar_okunamadi },
  sermaye_hareketi_haric: { tone: "info", ...M.bicim.damga.sermaye_hareketi_haric },
  maliyet_eksik: { tone: "warn", ...M.bicim.damga.maliyet_eksik },
  kaydedilmemis_temettu: { tone: "warn", ...M.bicim.damga.kaydedilmemis_temettu },
  kaydedilmemis_bolunme: { tone: "danger", ...M.bicim.damga.kaydedilmemis_bolunme },
  bedelsiz_ruchan_bilinmiyor: { tone: "info", ...M.bicim.damga.bedelsiz_ruchan_bilinmiyor },
};

/** Dayanağın türü — damga satırında "neye dayanıyor" diye okunuyor. */
export const BASIS_KIND_LABEL: Readonly<Record<ValuationBasisKind, string>> = M.bicim.dayanak;

/**
 * Bir damgalı tutarın TEK SATIRLIK dayanağı.
 *
 * `asOf === null` sessizce boş bırakılmıyor: o durum "ölçüm yok" DEĞİL,
 * "bu sayı bir güne değerlenmiyor" demek (çevrilmiş maliyet, gerçekleşmiş
 * kazanç — hepsi geçmiş ölçümlere dayanır ve bayat olamaz). İkisini aynı
 * boşlukla göstermek, raporun en ince ayrımını (bkz. `ValuationBasis`
 * → `forAskedDate`) kullanıcıdan gizlerdi.
 */
/**
 * BAYATLIK METNİ — YALNIZCA ANORMALLİK VARSA.
 *
 * Anormallik yoksa `null` döner ve çağıran hiçbir şey çizmez. Eskiden bu
 * fonksiyon "· taze" de diyordu; her tutarın altında bir satır açıp
 * "bakılacak bir şey yok" diye yer kaplıyordu. **Damga bir anormalliğin
 * adıdır**: normal durumu bildiren damga, ekrandaki her işaretin anlamını
 * zayıflatır ve gerçek anormalliği kalabalıkta kaybeder.
 *
 * `asOf === null` de bir anormallik DEĞİL: tutar tamamen geçmiş ölçümlere
 * dayanıyor demektir (2019 maliyetini 2019 kuruyla çevirmek gibi) ve o
 * ölçümler doğrudur. Dayanak dökümünde "geçmiş ölçüm" diye zaten yazıyor.
 */
export function bayatlikMetni(value: {
  readonly asOf: string | null;
  readonly staleDays: number | null;
  readonly stale: boolean;
  readonly basis: readonly ValuationBasis[];
}): string | null {
  // P-c: HÜKÜM MOTORUN (`stale`). Eskiden burada `staleDays > 0` vardı ve
  // cumartesi günü cuma fiyatı sarı yanıyordu — TEFAS cumartesi yayımlamaz.
  if (value.asOf === null || !value.stale) return null;
  // Balonun ilk satırı EN BAYAT bayat dayanağı anlatıyor: toplamın tazeliği
  // o bileşen kadar (`stamp`).
  let enBayat: ValuationBasis | null = null;
  for (const b of value.basis) {
    if (!b.forAskedDate || !b.stale) continue;
    if (enBayat === null || b.asOf < enBayat.asOf) enBayat = b;
  }
  const asOf = enBayat?.asOf ?? value.asOf;
  // P-d: anlık fiyatsa SAATİYLE (K3) — "01.10.2026 14:15 ölçümüyle".
  const olcum = enBayat?.asOfTs === undefined ? gunMetni(asOf) : anMetni(enBayat.asOfTs);
  return M.bicim.olcumuyle(olcum, beklentiMetni(enBayat, value.staleDays));
}

/**
 * Bayat bir dayanağın "ne bekleniyordu" kısmı. Takvimi olan kaynakta
 * BEKLENEN GÜN söylenir ("28.09.2026 bekleniyordu") — "3 gün bayat" demek
 * hafta sonunu da sayar ve pazartesi sabahı yanlış bir büyüklük verir.
 * Takvimsiz kaynakta (Yahoo, elle fiyat) eski metin.
 */
export function beklentiMetni(
  b: Pick<ValuationBasis, "expectedAsOf" | "staleDays" | "expectedObservedAt"> | null,
  staleDays: number | null,
): string {
  // P-d: gün içi hüküm — merkezin son yuvada fiyatı OKUMUŞ olması bekleniyordu.
  if (b !== null && b.expectedObservedAt !== undefined) {
    return M.bicim.guncellemesiBekleniyordu(anMetni(b.expectedObservedAt));
  }
  if (b !== null && b.expectedAsOf !== null) return M.bicim.bekleniyordu(gunMetni(b.expectedAsOf));
  return M.bicim.gunBayat(b?.staleDays ?? staleDays ?? 0);
}

/**
 * P-d · Bir anın gösterimi: "01.10.2026 14:15". Dilim raporunki (İstanbul) —
 * ABD hissesinin saati de kullanıcının saatiyle okunur; piyasanın yerel
 * saatini göstermek, yan yana duran iki satırı iki farklı saatle yazmak olurdu.
 */
export function anMetni(iso: string): string {
  const d = new Date(iso);
  const gun = d.toLocaleDateString(YEREL, { timeZone: "Europe/Istanbul" });
  const saat = d.toLocaleTimeString(YEREL, {
    timeZone: "Europe/Istanbul",
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${gun} ${saat}`;
}

function gunMetni(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(YEREL, { timeZone: "UTC" });
}
